import { getErrorMessage, useServices, useToast } from '@/app/providers';
import type { AutoBlockConfig } from '@yawi/types';
import { useCallback, useEffect, useState } from 'react';

/**
 * Carga la config de bloqueo automático (para el badge del header y el modal)
 * y la guarda. `enabled = false` no la carga (el admin de sección no tiene acceso).
 */
export function useAutoBlockConfig(enabled = true) {
  const { cobranzaRepo } = useServices();
  const toast = useToast();

  const [config, setConfig] = useState<AutoBlockConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setConfig(await cobranzaRepo.getAutoBlockConfig());
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [cobranzaRepo]);

  useEffect(() => {
    if (enabled) void load();
  }, [load, enabled]);

  const save = async (next: AutoBlockConfig): Promise<boolean> => {
    setSaving(true);
    try {
      await cobranzaRepo.saveAutoBlockConfig(next);
      setConfig(next);
      toast.success('Configuración guardada');
      return true;
    } catch {
      toast.error('No se pudo guardar la configuración');
      return false;
    } finally {
      setSaving(false);
    }
  };

  return { config, loading, saving, error, save, reload: load };
}
